import { useEffect, useRef } from 'react';
import { useCurrentTenantId } from '@/contexts/TenantContext';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { preferencesAPI } from '@/lib/supabase/preferences';

/**
 * Persist a page's filter selection per user, across devices.
 *
 * Hybrid storage:
 *  - localStorage (`vehix_prefs_<tenant>_<page>`) gives an instant, no-flicker
 *    restore on load and an offline fallback.
 *  - prod.user_preferences (preference_type 'recall_filters', entity_id = pageKey)
 *    is the cross-device source of truth, read PER USER. On load we paint from
 *    localStorage first, then reconcile from the DB (DB wins) unless the user has
 *    already changed a filter. Saves write through to both (DB best-effort).
 *
 * NOTE: This intentionally mirrors the async + localStorage-cache pattern used by
 * useDealerPreferences / useCampaignPriorities rather than React Query — that is
 * the established pattern for prod.user_preferences and keeps behavior consistent.
 *
 * The caller passes a serializable `snapshot` of its current filter state and an
 * `onRestore` callback that applies a saved snapshot back onto state.
 */
const PREFS_PREFIX = 'vehix_prefs_';
const SAVE_DEBOUNCE_MS = 400;

/* eslint-disable max-lines-per-function */
export function usePersistedFilters<T extends object>(
  pageKey: string,
  snapshot: T,
  onRestore: (restored: Partial<T>) => void
): void {
  const tenantId = useCurrentTenantId();
  const { session } = useAuth();
  const userId = session?.user?.id;

  // Keep the latest onRestore without making it an effect dependency.
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;

  // Hydration / change tracking (reset when the tenant or user changes).
  const localDoneRef = useRef(false);
  const dbStartedRef = useRef(false);
  const dbDoneRef = useRef(false);
  const userChangedRef = useRef(false);
  const hadLocalRef = useRef(false);
  const lastSerializedRef = useRef<string | null>(null);

  const storageKey = tenantId ? `${PREFS_PREFIX}${tenantId}_${pageKey}` : null;

  // Reset hydration state when the context (tenant/user) changes so the new
  // context re-restores instead of reusing the previous user's hydration.
  useEffect(() => {
    localDoneRef.current = false;
    dbStartedRef.current = false;
    dbDoneRef.current = false;
    userChangedRef.current = false;
    hadLocalRef.current = false;
    lastSerializedRef.current = null;
  }, [tenantId, userId]);

  // 1) Instant restore from localStorage (no flicker).
  useEffect(() => {
    if (!storageKey || localDoneRef.current) return;
    localDoneRef.current = true;
    try {
      const raw = localStorage.getItem(storageKey);
      hadLocalRef.current = !!raw;
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          onRestoreRef.current(parsed as Partial<T>);
        }
      }
    } catch {
      // Corrupt entry — ignore; it'll be overwritten on next save.
    }
  }, [storageKey]);

  // 2) Reconcile from the DB (source of truth) once user + tenant are known.
  useEffect(() => {
    if (!userId || !tenantId || dbStartedRef.current) return;
    dbStartedRef.current = true;
    let cancelled = false;
    (async () => {
      const dbValue = await preferencesAPI.getRecallFilters(userId, pageKey);
      if (cancelled) return;
      dbDoneRef.current = true;
      if (dbValue && !userChangedRef.current) {
        // DB wins on load (unless the user already changed something).
        onRestoreRef.current(dbValue as Partial<T>);
        lastSerializedRef.current = JSON.stringify(dbValue);
        if (storageKey) {
          try {
            localStorage.setItem(storageKey, JSON.stringify(dbValue));
          } catch {
            // best-effort cache
          }
        }
      } else if (!dbValue && hadLocalRef.current && storageKey) {
        // Seed the DB from the local value so existing users keep their prefs.
        try {
          const raw = localStorage.getItem(storageKey);
          if (raw) await preferencesAPI.saveRecallFilters(userId, pageKey, JSON.parse(raw));
        } catch {
          // best-effort seed
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, tenantId, pageKey, storageKey]);

  // 3) Write-through save (debounced) on real user changes only.
  const serialized = JSON.stringify(snapshot);
  useEffect(() => {
    if (!localDoneRef.current) return undefined; // nothing restored yet
    // While the DB reconcile is still pending for an authed user, keep the
    // baseline current but don't save (avoids racing the DB load).
    if (userId && !dbDoneRef.current) {
      lastSerializedRef.current = serialized;
      return undefined;
    }
    if (lastSerializedRef.current === null) {
      lastSerializedRef.current = serialized; // baseline (restored/default) — don't save
      return undefined;
    }
    if (serialized === lastSerializedRef.current) return undefined; // no real change

    userChangedRef.current = true;
    lastSerializedRef.current = serialized;
    if (!storageKey) return undefined;

    const timer = setTimeout(() => {
      try {
        localStorage.setItem(storageKey, serialized);
      } catch {
        // quota/serialization — best effort
      }
      if (userId) {
        // Background, best-effort; localStorage already holds the value.
        preferencesAPI
          .saveRecallFilters(userId, pageKey, JSON.parse(serialized) as Record<string, unknown>)
          .catch(() => undefined);
      }
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [serialized, storageKey, userId, pageKey]);
}

export default usePersistedFilters;
